import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@shared/contexts/AuthContext";
import { Button } from "@shared/components/ui/button";
import { useAuditEvent, useAuditList } from "@shared/api/hooks/useAudit";
import {
  exportAuditCsv,
  type AuditEvent,
  type AuditFilters as Filters,
} from "@shared/api/audit";
import AuditFilters from "./AuditFilters";
import AuditTable, {
  EXTRA_COLUMN_LABELS,
  type ExtraColumn,
} from "./AuditTable";
import AuditDetailDrawer from "./AuditDetailDrawer";

// F-E Phase 4b — admin-only audit browser. Reads Mongo via /api/staff/audit
// (no ES dependency, 24/7). Filters are URL-synced (shareable); pagination is
// forward-cursor with a page pointer over cached pages (Older/Newer, batch L).

const FILTER_KEYS = [
  "actorId",
  "action",
  "resourceKind",
  "resourceId",
  "decision",
  "from",
  "to",
] as const;

const EXTRAS_STORAGE_KEY = "audit.extraColumns";

function daysAgoIso(days: number): string {
  return new Date(Date.now() - days * 24 * 3600 * 1000).toISOString();
}

const AuditLog: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Admin gate (ProtectedRoute only checks auth, not role).
  useEffect(() => {
    if (!isAdmin) {
      toast.error("Admin access required");
      navigate("/staff/exhibitions");
    }
  }, [isAdmin, navigate]);

  // Default to the last 24h on first open (2026-07-27; was 7d).
  // Open-ended (2026-07-24): only `from` is set — a pinned `to` froze at
  // page-open time and silently hid everything that happened afterwards
  // (including the export the admin just triggered). No `to` = "…onward".
  useEffect(() => {
    if (!searchParams.get("from") && !searchParams.get("to")) {
      const next = new URLSearchParams(searchParams);
      next.set("from", daysAgoIso(1));
      setSearchParams(next, { replace: true });
    }
  }, []);

  const filters: Filters = useMemo(() => {
    const f: Filters = {};
    for (const k of FILTER_KEYS) {
      const v = searchParams.get(k);
      if (v) (f as Record<string, string>)[k] = v;
    }
    return f;
  }, [searchParams]);

  const updateFilters = (partial: Partial<Filters>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(partial)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    next.delete("event");
    setSearchParams(next);
  };

  const resetFilters = () => setSearchParams(new URLSearchParams());

  // ── data + pagination ────────────────────────────────────────────────
  const query = useAuditList(filters);
  const pages = query.data?.pages ?? [];
  const [pageIndex, setPageIndex] = useState(0);
  const filtersKey = JSON.stringify(filters);
  useEffect(() => setPageIndex(0), [filtersKey]);

  const events = pages[pageIndex]?.items ?? [];
  const canNewer = pageIndex > 0;
  const canOlder = pageIndex < pages.length - 1 || Boolean(query.hasNextPage);

  const goOlder = async () => {
    if (pageIndex < pages.length - 1) {
      setPageIndex((i) => i + 1);
    } else if (query.hasNextPage) {
      await query.fetchNextPage();
      setPageIndex((i) => i + 1);
    }
  };

  // ── drawer (row select + ?event deep link) ───────────────────────────
  const [selected, setSelected] = useState<AuditEvent | null>(null);
  const deepLinkId = searchParams.get("event") ?? undefined;
  const { data: deepEvent } = useAuditEvent(selected ? undefined : deepLinkId);
  useEffect(() => {
    if (deepEvent) setSelected(deepEvent);
  }, [deepEvent]);

  const closeDrawer = () => {
    setSelected(null);
    if (searchParams.get("event")) {
      const next = new URLSearchParams(searchParams);
      next.delete("event");
      setSearchParams(next, { replace: true });
    }
  };

  // ── extra columns (localStorage) ─────────────────────────────────────
  const [extras, setExtras] = useState<ExtraColumn[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(EXTRAS_STORAGE_KEY) || "[]");
    } catch {
      return [];
    }
  });
  useEffect(() => {
    localStorage.setItem(EXTRAS_STORAGE_KEY, JSON.stringify(extras));
  }, [extras]);
  const toggleExtra = (col: ExtraColumn) =>
    setExtras((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col],
    );

  // ── export ───────────────────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);
  const onExport = async () => {
    setExporting(true);
    try {
      const { blob, truncated } = await exportAuditCsv(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "audit-export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
      if (truncated) {
        toast.message(
          "Matched more than 100,000 events; exported the first 100,000. Narrow filters for the full set.",
        );
      }
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="audit-log-page">
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate("/staff/exhibitions")}
              variant="ghost"
              size="sm"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-heading font-bold">Log</h1>
          </div>
          <div className="flex items-center gap-2">
            <details className="relative">
              <summary
                className="list-none cursor-pointer text-sm border border-border rounded-md px-3 py-2"
                data-testid="audit-columns-menu"
              >
                Columns ▾
              </summary>
              <div className="absolute right-0 mt-1 z-10 bg-white border border-border rounded-md p-2 shadow-md">
                {(Object.keys(EXTRA_COLUMN_LABELS) as ExtraColumn[]).map(
                  (c) => (
                    <label
                      key={c}
                      className="flex items-center gap-2 text-sm py-1 whitespace-nowrap"
                    >
                      <input
                        type="checkbox"
                        checked={extras.includes(c)}
                        onChange={() => toggleExtra(c)}
                        data-testid={`audit-column-toggle-${c}`}
                      />
                      {EXTRA_COLUMN_LABELS[c]}
                    </label>
                  ),
                )}
              </div>
            </details>
            <Button
              onClick={onExport}
              disabled={exporting}
              data-testid="audit-export-button"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
        <AuditFilters
          filters={filters}
          onChange={updateFilters}
          onReset={resetFilters}
        />

        {query.isLoading ? (
          <div className="text-center py-12" data-testid="loading-state">
            Loading...
          </div>
        ) : query.isError ? (
          <div
            className="text-center py-12 text-destructive border border-border rounded-lg"
            data-testid="error-state"
          >
            Audit store is temporarily unavailable. Try again in a moment.
          </div>
        ) : events.length === 0 ? (
          <div
            className="text-center py-12 border border-border rounded-lg"
            data-testid="empty-state"
          >
            <p className="text-muted-foreground">
              No events match these filters. Try widening the date range or
              removing filters.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={resetFilters}
              data-testid="empty-reset-button"
            >
              Reset
            </Button>
          </div>
        ) : (
          <>
            <AuditTable
              events={events}
              extras={extras}
              onSelect={setSelected}
              onFilterActor={(actorId) => updateFilters({ actorId })}
              onFilterAction={(action) => updateFilters({ action })}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => canNewer && setPageIndex((i) => i - 1)}
                disabled={!canNewer}
                data-testid="audit-newer-button"
              >
                ← Newer
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={goOlder}
                disabled={!canOlder || query.isFetchingNextPage}
                data-testid="audit-older-button"
              >
                Older →
              </Button>
            </div>
          </>
        )}
      </div>

      <AuditDetailDrawer event={selected} onClose={closeDrawer} />
    </div>
  );
};

export default AuditLog;
